import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  getMissionDashboardSummary,
  getMissionsPayload,
  isMissionsAuthError,
} from '../../services/missionsApi';
import { useMissionsSession } from './useMissionsSession';
import { formatNumber } from './missionUtils';

function SummaryCard({ label, value, bgcolor = '#fafafa' }) {
  return (
    <Paper sx={{ p: 2, textAlign: 'center', bgcolor, height: '100%' }}>
      <Typography variant="h4" fontWeight={700}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Paper>
  );
}

function MissionDashboardSummary() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [authError, setAuthError] = useState(false);
  const [summary, setSummary] = useState(null);
  const { handleApiResponse } = useMissionsSession(setError);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError('');
    setAuthError(false);

    try {
      const result = await getMissionDashboardSummary();
      setAuthError(isMissionsAuthError(result));
      if (handleApiResponse(result, 'Failed to load mission dashboard summary.')) {
        setSummary(null);
        return;
      }
      setSummary(getMissionsPayload(result));
    } catch (fetchError) {
      setError(fetchError.message || 'Failed to load mission dashboard summary.');
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [handleApiResponse]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const chartData = useMemo(() => {
    const items = summary?.active_by_mission || [];
    return items
      .slice()
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
      .map((item) => ({
        name: `#${item.sort_order} ${(item.mission_name || '').slice(0, 18)}`,
        fullName: item.mission_name,
        active: item.active_count ?? 0,
        mission_id: item.mission_id,
      }));
  }, [summary]);

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Participant Dashboard</Typography>
        <IconButton onClick={fetchSummary} disabled={loading} title="Refresh summary">
          <RefreshIcon />
        </IconButton>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            authError ? (
              <Button color="inherit" size="small" href="/login">
                Log in
              </Button>
            ) : null
          }
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <SummaryCard
                label="Total Participants"
                value={formatNumber(summary?.total_participants)}
                bgcolor="#EEF2FF"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SummaryCard
                label="Active Assignments"
                value={formatNumber(summary?.active_assignments)}
                bgcolor="#ECFDF5"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SummaryCard
                label="Completed Assignments"
                value={formatNumber(summary?.completed_assignments)}
                bgcolor="#F0FDF4"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SummaryCard
                label="Completed (24h)"
                value={formatNumber(summary?.completed_last_24h)}
                bgcolor="#FFF7ED"
              />
            </Grid>
          </Grid>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Active Users by Mission
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Funnel view — users currently stuck on each mission step
              </Typography>
              {chartData.length === 0 ? (
                <Typography color="text.secondary">No active mission data yet.</Typography>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      angle={-35}
                      textAnchor="end"
                      interval={0}
                      height={80}
                      tick={{ fontSize: 11 }}
                    />
                    <YAxis allowDecimals={false} />
                    <Tooltip
                      formatter={(value) => [formatNumber(value), 'Active users']}
                      labelFormatter={(_, payload) =>
                        payload?.[0]?.payload?.fullName || ''
                      }
                    />
                    <Bar dataKey="active" fill="#4F46E5" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {chartData.length > 0 && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Mission Funnel
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Order</TableCell>
                        <TableCell>Mission</TableCell>
                        <TableCell align="right">Active Users</TableCell>
                        <TableCell align="right">Filter</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {chartData.map((row) => (
                        <TableRow key={row.mission_id} hover>
                          <TableCell>#{row.name.split(' ')[0].replace('#', '')}</TableCell>
                          <TableCell>{row.fullName}</TableCell>
                          <TableCell align="right">{formatNumber(row.active)}</TableCell>
                          <TableCell align="right">
                            <Button
                              component={Link}
                              to={`/admin/missions/participants?mission_id=${row.mission_id}&status=active`}
                              size="small"
                            >
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Box>
  );
}

export default MissionDashboardSummary;
